import { get, onValue, ref, remove } from 'firebase/database';
import { auth, db } from '../firebase';
import { runSafeTransaction, validateTransactionValue } from './safeTransaction';

const SCHEMA = 1;
const LEASE_MS = 120000;
const MAX_SECTION_BYTES = 8 * 1024 * 1024;
const MAX_ACCOUNT_BYTES = 12 * 1024 * 1024;
const completedDays = new Set();
const inFlight = new Map();
const clone = value => JSON.parse(JSON.stringify(value));
const byteLength = value => new TextEncoder().encode(value).length;
const emptyCatalog = () => ({ schemaVersion: SCHEMA });
const active = pending => pending && pending.expiresAt > Date.now();

function owner() {
  const uid = auth.currentUser?.uid;
  if (!uid || /[.#$[\]/]/.test(uid)) throw new Error('Sign in to manage recovery snapshots.');
  return uid;
}

function checkOwner(uid) {
  if (owner() !== uid) throw new Error('Your account changed. Reload before continuing.');
}

const backupRef = (uid, path) => ref(db, `noliBackups/${uid}/${path}`);
const token = () => crypto.randomUUID();

export function localBackupDay(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

// Sorted serialization makes identical collections share one immutable version.
export function canonicalBackupJSON(value) {
  if (Array.isArray(value)) return `[${value.map(canonicalBackupJSON).join(',')}]`;
  if (value && typeof value === 'object') return `{${Object.keys(value).sort().map(key => `${JSON.stringify(key)}:${canonicalBackupJSON(value[key])}`).join(',')}}`;
  return JSON.stringify(value);
}

async function hashJSON(json) {
  if (!crypto.subtle) throw new Error('Recovery snapshots require HTTPS or localhost.');
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(json));
  return Array.from(new Uint8Array(bytes), byte => byte.toString(16).padStart(2, '0')).join('');
}

function accountData(value, allowEmpty = false) {
  if (value == null && allowEmpty) return {};
  if (!value || Array.isArray(value) || typeof value !== 'object') throw new Error('No saved account data was found. Existing snapshots were kept.');
  const data = { ...value };
  delete data._restoreReceipt;
  if (!Object.keys(data).length && !allowEmpty) throw new Error('An empty account cannot replace a recovery snapshot.');
  validateTransactionValue(data);
  return data;
}

export async function describeBackup(value, { id = token(), date = localBackupDay(), kind = 'daily' } = {}) {
  const data = accountData(value, kind === 'before-restore');
  const sections = {};
  const versions = {};
  let totalBytes = 0;
  for (const [name, content] of Object.entries(data)) {
    const json = canonicalBackupJSON(content);
    const bytes = byteLength(json);
    totalBytes += bytes;
    if (bytes > MAX_SECTION_BYTES || totalBytes > MAX_ACCOUNT_BYTES) throw new Error('This account is too large for in-app snapshots (12 MB total, 8 MB per section). Export it separately; existing snapshots were kept.');
    const hash = await hashJSON(json);
    sections[name] = { hash, bytes };
    versions[name] = { json, bytes };
  }
  return { manifest: { id, date, kind, createdAt: Date.now(), totalBytes, sections }, versions };
}

export function listBackupSnapshots(catalog) {
  return Object.values(catalog?.days || {}).filter(Boolean).sort((a, b) => b.date.localeCompare(a.date));
}

export function backupStorageBytes(catalog) {
  const unique = new Map();
  [...listBackupSnapshots(catalog), catalog?.safety].filter(Boolean).forEach(snapshot => {
    Object.entries(snapshot.sections || {}).forEach(([name, version]) => unique.set(`${name}/${version.hash}`, version.bytes));
  });
  return [...unique.values()].reduce((sum, bytes) => sum + bytes, 0);
}

function references(catalog) {
  const result = new Map();
  [...listBackupSnapshots(catalog), catalog?.safety].filter(Boolean).forEach(snapshot => {
    Object.entries(snapshot.sections || {}).forEach(([section, version]) => result.set(`${section}__${version.hash}`, { section, hash: version.hash }));
  });
  return result;
}

function rotateDays(catalog, manifest) {
  const snapshots = [manifest, ...listBackupSnapshots(catalog).filter(item => item.date !== manifest.date)]
    .sort((a, b) => b.date.localeCompare(a.date)).slice(0, 3);
  return Object.fromEntries(snapshots.map((item, index) => [`d${index}`, item]));
}

export async function loadBackupCatalog() {
  const uid = owner();
  const snapshot = await get(backupRef(uid, 'catalog'));
  checkOwner(uid);
  const catalog = snapshot.val() || emptyCatalog();
  if (catalog.schemaVersion !== SCHEMA) throw new Error('These snapshots use a newer format. Update NoLi before continuing.');
  return catalog;
}

export function subscribeBackupCatalog(uid, next, failed) {
  if (uid !== owner()) throw new Error('Your account changed.');
  return onValue(backupRef(uid, 'catalog'), snapshot => {
    if (auth.currentUser?.uid === uid) next(snapshot.val() || emptyCatalog());
  }, failed);
}

export function subscribeRestoreReceipt(uid, next, failed) {
  if (uid !== owner()) throw new Error('Your account changed.');
  return onValue(ref(db, `scavengerData/${uid}/_restoreReceipt`), snapshot => {
    if (auth.currentUser?.uid === uid) next(snapshot.val());
  }, failed);
}

// SDK get() can fall back to cached data offline. Captures must use a successful
// authenticated server read, never a failed read or a partially loaded UI tree.
async function readLiveAccount(uid, allowEmpty = false) {
  checkOwner(uid);
  const idToken = await auth.currentUser.getIdToken();
  checkOwner(uid);
  const url = new URL(ref(db, `scavengerData/${uid}`).toString());
  const namespace = new URL(db.app?.options?.databaseURL || url.toString()).searchParams.get('ns');
  if (namespace) url.searchParams.set('ns', namespace);
  url.pathname += '.json';
  url.searchParams.set('auth', idToken);
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 20000);
  try {
    const response = await fetch(url.toString(), { cache: 'no-store', signal: controller.signal });
    if (!response.ok) throw new Error('Could not read saved account data from Firebase. No snapshot was replaced.');
    const value = await response.json();
    checkOwner(uid);
    return accountData(value, allowEmpty);
  } finally {
    clearTimeout(timeout);
  }
}

async function claim(uid, kind, date, target) {
  const pending = { id: token(), kind, expiresAt: Date.now() + LEASE_MS, date, ...(target ? { target } : {}) };
  const result = await runSafeTransaction(backupRef(uid, 'catalog'), current => {
    const catalog = current || emptyCatalog();
    if (catalog.schemaVersion !== SCHEMA) throw new Error('Update NoLi to manage these snapshots.');
    if (active(catalog.pending)) return undefined;
    if (kind === 'daily' && catalog.lastDailyDate >= date) return undefined;
    return { ...catalog, pending };
  }, { allowCreate: true });
  return result.committed ? { pending, catalog: result.snapshot.val() } : null;
}

function requireClaim(catalog, pending) {
  if (catalog?.pending?.id !== pending.id || !active(catalog.pending)) throw new Error('The snapshot operation expired or changed. No restore was applied.');
}

async function storeVersions(uid, bundle, catalog, pending) {
  const known = references(catalog);
  // Journal candidate keys before uploading. Interrupted captures can then clean
  // up their unreferenced blobs without listing/downloading all backup content.
  await runSafeTransaction(backupRef(uid, 'catalog'), current => {
    requireClaim(current, pending);
    const garbage = { ...current.garbage };
    Object.entries(bundle.manifest.sections).forEach(([section, version]) => {
      const key = `${section}__${version.hash}`;
      if (!known.has(key)) garbage[key] = { section, hash: version.hash };
    });
    return { ...current, garbage: Object.keys(garbage).length ? garbage : null };
  });
  for (const [section, version] of Object.entries(bundle.manifest.sections)) {
    checkOwner(uid);
    if (known.has(`${section}__${version.hash}`)) continue;
    await runSafeTransaction(backupRef(uid, `versions/${section}/${version.hash}`), current => {
      if (current) {
        if (current.json !== bundle.versions[section].json) throw new Error('A recovery version failed its integrity check.');
        return undefined;
      }
      return { ...bundle.versions[section], createdAt: Date.now() };
    }, { allowCreate: true });
  }
}

async function publish(uid, pending, bundle) {
  return runSafeTransaction(backupRef(uid, 'catalog'), current => {
    requireClaim(current, pending);
    const next = pending.kind === 'daily'
      ? { ...current, days: rotateDays(current, bundle.manifest), lastDailyDate: pending.date, pending: null }
      : { ...current, safety: bundle.manifest, pending: { ...current.pending, ready: true, safetyId: bundle.manifest.id } };
    const kept = references(next);
    const garbage = { ...current.garbage };
    references(current).forEach((entry, key) => { if (!kept.has(key)) garbage[key] = entry; });
    kept.forEach((_, key) => { delete garbage[key]; });
    next.garbage = Object.keys(garbage).length ? garbage : null;
    return next;
  });
}

async function release(uid, pending, lastRestore) {
  checkOwner(uid);
  await runSafeTransaction(backupRef(uid, 'catalog'), current => {
    if (current?.pending?.id !== pending.id) return undefined;
    return { ...current, pending: null, ...(lastRestore ? { lastRestore } : {}) };
  });
}

async function collectGarbage(uid, catalog) {
  // Only versions from expired slots are candidates. Rules independently reject
  // deletion of a version referenced by any current slot or safety snapshot.
  for (const [key, entry] of Object.entries(catalog.garbage || {})) {
    checkOwner(uid);
    try {
      await remove(backupRef(uid, `versions/${entry.section}/${entry.hash}`));
      await runSafeTransaction(backupRef(uid, 'catalog'), current => {
        if (!current?.garbage?.[key] || active(current.pending)) return undefined;
        const garbage = { ...current.garbage };
        delete garbage[key];
        return { ...current, garbage: Object.keys(garbage).length ? garbage : null };
      });
    } catch {
      // Retry recorded candidates on the next daily capture; never prune a slot.
    }
  }
}

async function daily(uid, date) {
  const catalog = await loadBackupCatalog();
  if (catalog.lastDailyDate >= date) return { status: 'already-saved', catalog };
  const lease = await claim(uid, 'daily', date);
  if (!lease) return { status: 'busy', catalog };
  let published = false;
  try {
    const bundle = await describeBackup(await readLiveAccount(uid), { date });
    await storeVersions(uid, bundle, lease.catalog, lease.pending);
    const result = await publish(uid, lease.pending, bundle);
    published = true;
    await collectGarbage(uid, result.snapshot.val());
    return { status: 'saved', catalog: result.snapshot.val() };
  } finally {
    if (!published && auth.currentUser?.uid === uid) await release(uid, lease.pending).catch(() => {});
  }
}

export async function ensureDailyBackup(date = localBackupDay()) {
  const uid = owner();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) throw new Error('Invalid snapshot day.');
  const key = `${uid}:${date}`;
  if (completedDays.has(key)) return { status: 'already-saved' };
  if (inFlight.has(key)) return inFlight.get(key);
  const task = daily(uid, date).then(result => {
    if (result.status !== 'busy') completedDays.add(key);
    return result;
  });
  inFlight.set(key, task);
  try { return await task; } finally { inFlight.delete(key); }
}

function findSnapshot(catalog, id) {
  const snapshot = [...listBackupSnapshots(catalog), catalog.safety].find(item => item?.id === id);
  if (!snapshot) throw new Error('This snapshot is no longer available. Refresh the list.');
  return snapshot;
}

async function readSnapshot(uid, manifest) {
  validateTransactionValue(manifest);
  if (!manifest.sections || !Object.keys(manifest.sections).length) throw new Error('The snapshot is empty or incomplete.');
  const data = {};
  let total = 0;
  for (const [section, version] of Object.entries(manifest.sections)) {
    checkOwner(uid);
    if (!/^[a-f0-9]{64}$/.test(version.hash)) throw new Error('Invalid recovery version.');
    const snapshot = await get(backupRef(uid, `versions/${section}/${version.hash}`));
    const saved = snapshot.val();
    if (!saved || typeof saved.json !== 'string' || byteLength(saved.json) !== version.bytes || await hashJSON(saved.json) !== version.hash) throw new Error('A snapshot section is missing or damaged. Nothing was restored.');
    total += version.bytes;
    if (total > MAX_ACCOUNT_BYTES) throw new Error('The snapshot exceeds the safe restore limit.');
    data[section] = JSON.parse(saved.json);
  }
  if (total !== manifest.totalBytes) throw new Error('The snapshot is incomplete. Nothing was restored.');
  checkOwner(uid);
  return accountData(data);
}

export async function previewBackupRestore(id) {
  const uid = owner();
  const manifest = findSnapshot(await loadBackupCatalog(), id);
  const data = await readSnapshot(uid, manifest);
  return { manifest, sections: Object.keys(data) };
}

export async function restoreAccountBackup(id, confirmation) {
  if (confirmation !== 'RESTORE') throw new Error('Type RESTORE to confirm replacing your NoLi data.');
  const uid = owner();
  const manifest = findSnapshot(await loadBackupCatalog(), id);
  const restored = await readSnapshot(uid, manifest);
  const lease = await claim(uid, 'restore', localBackupDay(), manifest);
  if (!lease) throw new Error('Another snapshot or restore is running. Wait and refresh.');
  let uncertain = false;
  let applied = false;
  try {
    const current = await readLiveAccount(uid, true);
    const fingerprint = canonicalBackupJSON(current);
    const safety = await describeBackup(current, { kind: 'before-restore' });
    await storeVersions(uid, safety, lease.catalog, lease.pending);
    await publish(uid, lease.pending, safety);
    checkOwner(uid);
    const receipt = { id: lease.pending.id, snapshotId: id, restoredAt: Date.now() };
    const result = await runSafeTransaction(ref(db, `scavengerData/${uid}`), value => {
      if (canonicalBackupJSON(accountData(value, true)) !== fingerprint) throw new Error('Account data changed during restore preparation. Nothing was restored. Refresh and try again.');
      return { ...clone(restored), _restoreReceipt: receipt };
    }, { allowCreate: true });
    if (!result.committed) throw new Error('Restore was not confirmed by Firebase. Refresh before trying again.');
    applied = true;
    await release(uid, lease.pending, receipt);
    return receipt;
  } catch (error) {
    uncertain = /awaiting server confirmation|still awaiting/i.test(error.message);
    if (applied) throw new Error('Data was restored, but final confirmation was interrupted. Reload NoLi; do not restore again.');
    throw error;
  } finally {
    // A timed-out SDK write can still be pending. Let its server-checked lease
    // expire rather than releasing it and allowing overlapping edits.
    if (!applied && !uncertain && auth.currentUser?.uid === uid) await release(uid, lease.pending).catch(() => {});
  }
}
